/**
 * `buildActivityRowFromEvent` — event-as-row builder for the unified
 * `useGetUserActivity` feed.
 *
 * The activity feed surfaces 7 kinds in one timestamp-sorted stream:
 *   - trade · claim · back · unback · boost                       (chain — typed market events on `.v0` subjects)
 *   - gift_sent · gift_received                                   (api/chain — `morfi.user.${u}.gift.v0`)
 *
 * The SDK manifest declares one `prepend:` recipe per subject, all pointing
 * here. We discriminate on the subject suffix because the payload shapes
 * vary across event families:
 *   - `morfi.market.{m}.user.{actor}.trade.v0`    → MarketTrade
 *   - `morfi.market.{m}.user.{actor}.backing.v0`  → MarketBacking (BACK | UNBACK by `side`)
 *   - `morfi.market.{m}.user.{actor}.boost.v0`    → MarketBoost
 *   - `morfi.market.{m}.user.{actor}.claim.v0`    → MarketPrizeClaim
 *   - `morfi.user.{u}.gift.v0`                    → GiftEvent (CREATED | CLAIMED)
 *
 * The wire payload carries only what the indexer puts on it; server-derived
 * fields (`opaqueId`, `counterpartyHandle`, `giftStatus`, `assetKind`,
 * `outcomeLabel`, `marketTitle`, `marketCoverImageUrl`, `marketCoverImageNsfw`)
 * are absent for chain rows and surface as `null`, as does `trader` on every
 * kind except trade, which reads it from the payload `actor`. Trade rows omit
 * the four market/outcome presentation fields instead of nulling them, so a
 * live trade can't erase the presentation the API stored under the same `id`.
 * The next REST refetch on staleTime backfills them via the activity-service
 * join. Honest partial row — mirrors the `buildTradeRowFromEvent` policy.
 *
 * Direction caveat for gifts: `morfi.user.{u}.gift.v0` is single-subject
 * today. The wire doesn't carry sender-vs-recipient direction from the
 * viewer's POV (publisher fan-outs to both sides on each event), so the
 * helper conservatively reports `kind: 'gift_received'` on the live row.
 * A follow-up split into `.gift.sent.v0` / `.gift.received.v0` (or a
 * `direction` field on `GiftEvent`) would let the helper emit the correct
 * kind per arrival. Until then the SDK consumer can re-pull on next
 * staleTime to get the right kind.
 *
 * Trade `priceD9` is the execution-average price, matching the REST activity
 * row. `postOutcomePriceD9` is a distinct post-reserve spot mark and must not
 * be substituted when an older producer omits `executionPriceD9`.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import { fromBase58 } from '../tx-engine/bytes.js';

const SIDE_BUY = 1 as const;
const SIDE_SELL = 2 as const;
const SIDE_UNBACK = 4 as const;

/**
 * Shape of one cached `useGetUserActivity` row (mirrors the codegen-emitted
 * `GetUserActivityRow` discriminated-but-flat shape). Declared by hand here
 * so the helper doesn't import generated code (codegen output imports
 * helpers — the reverse would be a cycle).
 */
export interface BuiltActivityRow {
  readonly kind: 'trade' | 'claim' | 'back' | 'unback' | 'boost' | 'gift_received' | 'gift_sent';
  readonly ts: string;
  readonly id: string;
  readonly market: string | null;
  readonly amountD6: string;
  readonly outcomeIndex: number | null;
  readonly side: 'buy' | 'sell' | null;
  readonly priceD9: string | null;
  readonly tradeEventId: string | null;
  readonly giftPda: string | null;
  readonly participationId: string | null;
  readonly opaqueId: string | null;
  readonly counterpartyHandle: string | null;
  readonly giftStatus: 'pending' | 'claimed' | 'withdrawn' | null;
  readonly assetKind: 'usdc' | 'outcome_token' | null;
  readonly trader: string | null;
  // Optional because live trade rows omit them: such a row shares the API's `id`,
  // and an absent field keeps the presentation already stored for it, where
  // `null` would overwrite it.
  readonly outcomeLabel?: string | null;
  readonly marketTitle?: string | null;
  readonly marketCoverImageUrl?: string | null;
  readonly marketCoverImageNsfw?: boolean | null;
}

/** Hex-encode a byte array (no `0x` prefix). Matches the `signature` id
 *  shape the activity backend emits for chain rows. */
function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}

/** Coerce an unknown D6 value to a base-10 string; nullish / malformed → '0'. */
function d6String(v: unknown): string {
  if (typeof v === 'bigint') return v.toString();
  if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v));
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return v.trim();
  return '0';
}

/** Coerce a nullable D9 value to a string or null. */
function d9StringOrNull(v: unknown): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v === 'bigint') return v.toString();
  if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v));
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return v.trim();
  return null;
}

/** Read a possibly nested payload field. */
function readField(raw: Record<string, unknown>, key: string): unknown {
  const nested = raw.payload;
  if (nested !== null && typeof nested === 'object' && key in (nested as object)) {
    return (nested as Record<string, unknown>)[key];
  }
  return raw[key];
}

/** Hex-encode the event's 15-byte `sequence_id` — the activity service's
 *  `tradeEventId`, not its row `id` (the transaction signature hex). Trade
 *  rows take their `id` from the signature instead (`tradeIdFromEvent`);
 *  every other live kind keeps this id until a refetch replaces the row. */
function deriveId(raw: Record<string, unknown>, fallback: string): string {
  const seq = readField(raw, 'sequenceId');
  if (seq instanceof Uint8Array && seq.length > 0) return bytesToHex(seq);
  return fallback;
}

/** Format the `ts` field — backend emits ISO 8601, wire is unix-seconds u64. */
function deriveTs(raw: Record<string, unknown>): string {
  const tsNum = Number(readField(raw, 'ts') ?? 0);
  const ms = tsNum > 1e12 ? tsNum : tsNum * 1000;
  return new Date(Number.isFinite(ms) && ms > 0 ? ms : Date.now()).toISOString();
}

/** Parse market base58 from a market-scoped subject. */
function marketFromMarketSubject(subject: string): string | null {
  const seg = subject.split('.');
  return seg[0] === 'morfi' && seg[1] === 'market' ? (seg[2] ?? null) : null;
}

/** Prefer the typed payload market and fall back to the market-scoped subject. */
function marketFromEvent(raw: Record<string, unknown>, subject: string): string | null {
  const payloadMarket = readField(raw, 'market');
  if (typeof payloadMarket === 'string' && payloadMarket.length > 0) return payloadMarket;
  return marketFromMarketSubject(subject);
}

/** Read the trade actor wallet; proto3 decodes an unset `actor` as an empty string. */
function traderFromEvent(raw: Parameters<typeof readField>[0]): string | null {
  const actor = readField(raw, 'actor');
  return typeof actor === 'string' && actor.length > 0 ? actor : null;
}

/** Key a live trade like the activity service does: the hex of its transaction signature. */
function tradeIdFromEvent(raw: Parameters<typeof readField>[0], fallback: string): string {
  const signature = readField(raw, 'signature');
  return typeof signature === 'string' && signature.length > 0
    ? bytesToHex(fromBase58(signature))
    : deriveId(raw, fallback);
}

function participationIdFromMarketSubject(
  subject: string,
  role: 'trader' | 'backer' | 'booster',
  market: string | null,
): string | null {
  const actor = subject.split('.')[4];
  if (market === null || actor === undefined || actor.length === 0) return null;
  return `${role}:${market}:${actor}`;
}

/**
 * Build a `useGetUserActivity` list row from a decoded realtime event.
 * `raw` is the protobuf decoded message; `subject` is the concrete wire
 * subject the event arrived on. Returns a fully-shaped `BuiltActivityRow`
 * — the SDK manifest's `prepend:` codegen unshifts it onto the cached
 * list head.
 */
export function buildActivityRowFromEvent(
  raw: Record<string, unknown>,
  subject: string,
  args?: Record<string, unknown>,
): BuiltActivityRow {
  const row = buildActivityRow(raw, subject);
  const kinds =
    typeof args?.kind === 'string'
      ? args.kind
          .split(',')
          .map((kind) => kind.trim())
          .filter((kind) => kind.length > 0)
      : [];
  const matchesKind = kinds.length === 0 || kinds.includes(row.kind);
  const matchesSide = row.kind !== 'trade' || args?.side === undefined || row.side === args.side;
  // The generated prepend recipe preserves the cache when the helper returns an empty id.
  return matchesKind && matchesSide ? row : { ...row, id: '' };
}

function buildActivityRow(raw: Record<string, unknown>, subject: string): BuiltActivityRow {
  const ts = deriveTs(raw);
  const amountD6 = d6String(readField(raw, 'amountD6'));
  const market = marketFromEvent(raw, subject);

  // morfi.market.<m>.user.<actor>.trade.v0
  if (subject.endsWith('.trade.v0')) {
    const sideRaw = Number(readField(raw, 'side') ?? 0);
    const side: 'buy' | 'sell' | null =
      sideRaw === SIDE_SELL ? 'sell' : sideRaw === SIDE_BUY ? 'buy' : null;
    const outcomeIdx = readField(raw, 'outcomeIdx');
    return {
      kind: 'trade',
      ts,
      id: tradeIdFromEvent(raw, `trade:${ts}`),
      market,
      amountD6,
      outcomeIndex: typeof outcomeIdx === 'number' ? outcomeIdx : null,
      side,
      priceD9: d9StringOrNull(readField(raw, 'executionPriceD9')),
      tradeEventId: deriveId(raw, `trade:${ts}`),
      giftPda: null,
      participationId: participationIdFromMarketSubject(subject, 'trader', market),
      opaqueId: null,
      counterpartyHandle: null,
      giftStatus: null,
      assetKind: null,
      trader: traderFromEvent(raw),
    };
  }

  // morfi.market.<m>.user.<actor>.backing.v0
  if (subject.endsWith('.backing.v0')) {
    const side = Number(readField(raw, 'side') ?? 0);
    const kind: 'back' | 'unback' = side === SIDE_UNBACK ? 'unback' : 'back';
    return {
      kind,
      ts,
      id: deriveId(raw, `${kind}:${ts}`),
      market,
      amountD6,
      outcomeIndex: null,
      side: null,
      priceD9: null,
      tradeEventId: null,
      giftPda: null,
      participationId: participationIdFromMarketSubject(subject, 'backer', market),
      opaqueId: null,
      counterpartyHandle: null,
      giftStatus: null,
      assetKind: null,
      trader: null,
      outcomeLabel: null,
      marketTitle: null,
      marketCoverImageUrl: null,
      marketCoverImageNsfw: null,
    };
  }

  // morfi.market.<m>.user.<actor>.boost.v0
  if (subject.endsWith('.boost.v0')) {
    return {
      kind: 'boost',
      ts,
      id: deriveId(raw, `boost:${ts}`),
      market,
      amountD6,
      outcomeIndex: null,
      side: null,
      priceD9: null,
      tradeEventId: null,
      giftPda: null,
      participationId: participationIdFromMarketSubject(subject, 'booster', market),
      opaqueId: null,
      counterpartyHandle: null,
      giftStatus: null,
      assetKind: null,
      trader: null,
      outcomeLabel: null,
      marketTitle: null,
      marketCoverImageUrl: null,
      marketCoverImageNsfw: null,
    };
  }

  // morfi.market.<m>.user.<actor>.claim.v0
  if (subject.endsWith('.claim.v0')) {
    const outcomeIdx = readField(raw, 'outcomeIdx');
    return {
      kind: 'claim',
      ts,
      id: deriveId(raw, `claim:${ts}`),
      market,
      amountD6,
      outcomeIndex: typeof outcomeIdx === 'number' ? outcomeIdx : null,
      side: null,
      priceD9: null,
      tradeEventId: null,
      giftPda: null,
      participationId: participationIdFromMarketSubject(subject, 'trader', market),
      opaqueId: null,
      counterpartyHandle: null,
      giftStatus: null,
      assetKind: null,
      trader: null,
      outcomeLabel: null,
      marketTitle: null,
      marketCoverImageUrl: null,
      marketCoverImageNsfw: null,
    };
  }

  // morfi.user.<u>.gift.v0 — GiftEvent. Direction (sent vs received) is
  // not on the wire today (see header note); default to gift_received.
  // The gift_config + mint + amount are on the payload.
  if (subject.endsWith('.gift.v0')) {
    const giftConfig = readField(raw, 'giftConfig');
    const k = Number(readField(raw, 'kind') ?? 0);
    const giftStatus: 'pending' | 'claimed' | 'withdrawn' | null = k === 2 ? 'claimed' : 'pending';
    return {
      kind: 'gift_received',
      ts,
      id: deriveId(raw, typeof giftConfig === 'string' ? giftConfig : `gift:${ts}`),
      market: null,
      amountD6,
      outcomeIndex: null,
      side: null,
      priceD9: null,
      tradeEventId: null,
      giftPda: null,
      participationId: null,
      opaqueId: null,
      counterpartyHandle: null,
      giftStatus,
      assetKind: null,
      trader: null,
      outcomeLabel: null,
      marketTitle: null,
      marketCoverImageUrl: null,
      marketCoverImageNsfw: null,
    };
  }

  // Unknown subject — should be unreachable (codegen pins the subject
  // templates). Surface as a `trade` row with `id` derived from sequence_id;
  // the activity service's next refetch will replace it with the truth.
  return {
    kind: 'trade',
    ts,
    id: deriveId(raw, `unknown:${ts}`),
    market,
    amountD6,
    outcomeIndex: null,
    side: null,
    priceD9: null,
    tradeEventId: null,
    giftPda: null,
    participationId: null,
    opaqueId: null,
    counterpartyHandle: null,
    giftStatus: null,
    assetKind: null,
    trader: null,
    outcomeLabel: null,
    marketTitle: null,
    marketCoverImageUrl: null,
    marketCoverImageNsfw: null,
  };
}
